import React, { useCallback } from 'react';
import * as Haptics from 'expo-haptics';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type MessageTag,
  type NoMessageValues,
} from 'react-intl';
import { Platform } from 'react-native';

import { theme } from '@garnish/constants';
import { Button, VStack } from '@garnish/core';

import { PermissionsPrompt } from '@sg/shared-fe-features-permissions';

// ─────────────────────────────────────────────────────────────────────────────

export const PermissionsPrimerLocationView = (props: Props) => {
  const { isRequesting, isSkipping, onAllow, onSkip } = props;

  const { formatMessage } = useIntl();

  // ─── Derived ─────────────────────────────────────────────────────────

  // Android allows a "Not now" button, while it is against Apple's guidelines on iOS.
  const isSkipAvailable = Platform.OS !== 'ios';

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleAllow = useCallback(() => {
    void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onAllow();
  }, [onAllow]);

  const handleSkip = useCallback(() => {
    void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onSkip();
  }, [onSkip]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <PermissionsPrompt.Title>
        <FormattedMessage {...messages.title} values={{ br: renderBreak }} />
      </PermissionsPrompt.Title>

      <PermissionsPrompt.Text>
        <FormattedMessage {...messages.text} />
      </PermissionsPrompt.Text>

      <VStack gap={theme.spacing['4']}>
        {isSkipAvailable ? (
          <Button
            onPress={handleSkip}
            palette="cream"
            size="large-wide"
            isLoading={isSkipping}
            disabled={isRequesting}
          >
            {formatMessage(messages.notNow)}
          </Button>
        ) : null}

        <Button
          onPress={handleAllow}
          palette="oatmeal"
          size="large-wide"
          disabled={isSkipping}
          isLoading={isRequesting}
        >
          {formatMessage(messages.allow)}
        </Button>
      </VStack>
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function renderBreak() {
  return '\n';
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly title: { readonly br: MessageTag };
  readonly text: NoMessageValues;
  readonly notNow: NoMessageValues;
  readonly allow: NoMessageValues;
}>({
  title: {
    defaultMessage: 'Enable<br></br>location for<br></br>faster ordering.',
    description: 'Permissions Primer | Location | Title',
  },
  text: {
    defaultMessage: 'Enable location settings for personalized offers and deals.',
    description: 'Permissions Primer | Location | Body text',
  },
  notNow: {
    defaultMessage: 'Not now',
    description: 'Permissions Primer | Location | Skip CTA',
  },
  allow: {
    defaultMessage: 'Continue',
    description: 'Permissions Primer | Location | Allow CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isRequesting: boolean;
  isSkipping: boolean;
  onAllow: () => void;
  onSkip: () => void;
};
